import React, { useEffect, useState } from "react";
import API_BASE_URL from "../../../components/apiconfig/api-config";
import NoRecordFound from "../../../components/common/no-record-found";
import { FaFilter, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const ROWS_PER_PAGE = 34;

const PurchaseContractRegister = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // ✅ Added contract date filters
  const [filters, setFilters] = useState({
    contractDateFrom: '',
    contractDateTo: ''
  });

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [search]);

  // ✅ Filter change handler
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  // ✅ Reset filters function
  const resetFilters = () => {
    setFilters({
      contractDateFrom: '',
      contractDateTo: ''
    });
    setPage(1);
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: debouncedSearch.trim(),
        contractDateFrom: filters.contractDateFrom,
        contractDateTo: filters.contractDateTo
      });

      const res = await fetch(
        `${API_BASE_URL}/api/report/purchase-contract-register?${params.toString()}`,
        {
          credentials: "include",
        }
      );

      if (!res.ok) throw new Error("Failed to fetch");
      const result = await res.json();

      setData(result.data || []);
      setTotal(result.total || result.data?.length || 0);
    } catch (err) {
      console.error("Error:", err);
      setError(
        "Failed to load purchase contract data. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  // ✅ Updated useEffect to include filters
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [debouncedSearch, page, filters]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="max-w-8xl mx-auto p-6 bg-white shadow-sm border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Purchase Contract Register
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            List of purchase contracts with suppliers and pricing
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-4 w-4 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  strokeWidth={2}
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by contract, supplier, material..."
              value={search}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition"
            />
          </div>
          {/* ✅ Filter Toggle Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-[#10a5e9] rounded-md hover:bg-blue-100 text-sm"
          >
            <FaFilter />
            {showFilters ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* ✅ Filter Section */}
      {showFilters && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col w-48">
              <label className="text-sm font-medium text-gray-700 mb-1">Contract Date From</label>
              <input
                type="date"
                name="contractDateFrom"
                value={filters.contractDateFrom}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              />
            </div>

            <div className="flex flex-col w-48">
              <label className="text-sm font-medium text-gray-700 mb-1">Contract Date To</label>
              <input
                type="date"
                name="contractDateTo"
                value={filters.contractDateTo}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              />
            </div>

            <div className="flex gap-2 mt-1">
              <button
                onClick={resetFilters}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
          <p className="text-sm text-gray-600">Loading purchase contracts...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
          <h3 className="text-sm font-medium text-red-800">
            Error loading data
          </h3>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      ) : data.length === 0 ? (
        <NoRecordFound
          message="No purchase contracts found"
          subMessage="Try changing your search or filters"
          iconClass="text-gray-400"
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-xs">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                    Contract No.
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                    Contract Date
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                    Supplier
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                    Commodity
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                    Total Quantity (MT)
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                    Price/MT (USD)
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                    Total Value (USD)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {data.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-[#3899cf]">
                      {row.contract_no}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.contract_date}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.supplier}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.material}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right font-mono">
                      {row.total_qty_mt}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right font-mono">
                      {row.unit_price_usd}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right font-mono">
                      {row.total_value_usd}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > ROWS_PER_PAGE && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white border-t border-gray-200 rounded-b-xl">
              <p className="text-sm text-gray-700">
                Showing {(page - 1) * ROWS_PER_PAGE + 1} to{" "}
                {Math.min(page * ROWS_PER_PAGE, total)} of {total} records
              </p>

              <div>
                <nav className="relative z-0 inline-flex shadow-sm rounded-md">
                  <button
                    onClick={handlePrevPage}
                    disabled={page === 1}
                    className={`px-4 py-2 border text-sm font-medium ${
                      page === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={page === totalPages}
                    className={`px-4 py-2 border text-sm font-medium ${
                      page === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PurchaseContractRegister;
